import { pb } from "@/lib/pocketbase";
import { pbRepository } from "./pocketbase-repository";
import { localTranscribeSupported } from "./local-transcribe.support";

/**
 * Mikro-Erfassung (Hausaufgaben/Stundenplan/Prüfungen per Sprache).
 *
 * Geschrieben werden die Zeilen NICHT hier, sondern serverseitig vom Hook
 * (`POST /api/ai/voice-suggestions`) – damit die Verarbeitung weiterläuft,
 * auch wenn die App zwischen Aufnahme-Ende und KI-Antwort geschlossen wird.
 * Dieser Service liest die Vorschläge nur, erlaubt Inline-Edits vor dem
 * Übernehmen und löscht (verwirft oder – nach Übernahme – räumt auf).
 */

export type VoiceDomain = "homework" | "timetable" | "exam";

export interface HomeworkSuggestion {
  subject: string;
  subjectTitle?: string;
  topic?: string;
  text: string;
  dueDate: string;
  duePeriod?: number;
  /**
   * Index in `context.candidates[subject]` (siehe HomeworkPage) statt eines
   * von der KI selbst berechneten Datums – wird beim Laden clientseitig in
   * dueDate/duePeriod aufgelöst (wie bei ExamSuggestion.candidateIndex).
   */
  candidateIndex?: number;
}

export interface TimetableSuggestion {
  action: "set" | "remove";
  weekday: number;
  period: number;
  subject?: string;
  subjectTitle?: string;
  room?: string;
}

export interface ExamSuggestion {
  subjectId: string;
  subjectTitle?: string;
  topicId?: string;
  topicTitle?: string;
  date: string;
  examType: string;
  periodFrom?: number;
  periodTo?: number;
  /**
   * Index in `context.candidates[subjectId]` (siehe ExamsPage) statt eines
   * von der KI selbst berechneten Datums – wird beim Laden clientseitig in
   * date/periodFrom/periodTo aufgelöst. So kann die KI kein falsches Jahr
   * erfinden: sie wählt nur eine Position in einer echten Stundenplan-Liste.
   */
  candidateIndex?: number;
}

export interface VoiceSuggestion<T = Record<string, unknown>> {
  id: string;
  domain: VoiceDomain;
  batch: string;
  payload: T;
  created: string;
}

const repo = pbRepository<Record<string, unknown>>("voice_suggestions");

function parsePayload(v: unknown): Record<string, unknown> {
  if (v && typeof v === "object") return v as Record<string, unknown>;
  if (typeof v === "string" && v.trim()) {
    try {
      return JSON.parse(v) as Record<string, unknown>;
    } catch {
      return {};
    }
  }
  return {};
}

function toSuggestion(r: Record<string, unknown>): VoiceSuggestion {
  return {
    id: String(r.id ?? ""),
    domain: r.domain as VoiceDomain,
    batch: String(r.batch ?? ""),
    payload: parsePayload(r.payload),
    created: String(r.created ?? ""),
  };
}

export const voiceService = {
  async listByDomain<T = Record<string, unknown>>(domain: VoiceDomain): Promise<VoiceSuggestion<T>[]> {
    const rows = await repo.list({
      filter: pb.filter("domain = {:d}", { d: domain }),
      sort: "created",
      perPage: 200,
    });
    return rows.map(toSuggestion) as VoiceSuggestion<T>[];
  },

  async updatePayload(id: string, payload: Record<string, unknown>): Promise<void> {
    await repo.update(id, { payload });
  },

  async remove(id: string): Promise<void> {
    await repo.remove(id);
  },

  /**
   * Aufnahme verarbeiten und Vorschläge erzeugen. Der Server fragt die KI und
   * schreibt die Vorschläge direkt in `voice_suggestions` – läuft weiter, auch
   * wenn die App währenddessen geschlossen wird. `batch` bei erneutem Aufnehmen
   * in derselben Sitzung wiederverwenden, damit die KI korrigiert statt
   * dupliziert.
   *
   * Erkannt wird nach Möglichkeit LOKAL (Whisper im Browser) – dann geht nur
   * noch der fertige Text zum Server statt einer mehrere hundert Kilobyte
   * großen Audiodatei. Das spart den Upload und die serverseitige Erkennung,
   * also genau die zwei langsamsten Schritte. Klappt das nicht (alter WebView,
   * Modell nicht ladbar), wandert wie bisher die Audiodatei hoch.
   */
  async record(domain: VoiceDomain, batch: string, audio: Blob, context: unknown): Promise<{ count: number; transcript: string }> {
    let transcript = "";
    if (localTranscribeSupported()) {
      try {
        const { transcribeLocal } = await import("./local-transcribe.service");
        transcript = await transcribeLocal(audio);
      } catch {
        transcript = "";
      }
    }

    if (transcript.trim()) {
      return pb.send<{ count: number; transcript: string }>("/api/ai/voice-suggestions", {
        method: "POST",
        body: { domain, batch, context: JSON.stringify(context ?? {}), transcript },
        requestKey: null,
      });
    }

    const fd = new FormData();
    const ext = audio.type.includes("mp4") ? "mp4" : audio.type.includes("ogg") ? "ogg" : "webm";
    fd.append("file", audio, `aufnahme.${ext}`);
    fd.append("domain", domain);
    fd.append("batch", batch);
    fd.append("context", JSON.stringify(context ?? {}));
    return pb.send<{ count: number; transcript: string }>("/api/ai/voice-suggestions", {
      method: "POST",
      body: fd,
      requestKey: null,
    });
  },
};
